import { darkTheme, lightTheme } from './theme/theme';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ShowableDialog from './shared/showable-dialog';
import SharedSnackbar from './shared/shared-snackbar';
import { LicenseInfo } from '@mui/x-license';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useStore } from './components/state/zustand-store';
import { Outlet, useLocation } from 'react-router-dom';
import { lazy, Suspense, useMemo } from 'react';
import GpanelLogoLight from './assets/gpanel-logo.png';
import { pagesConfig } from './shared/pages-config';
import { APIProvider } from '@vis.gl/react-google-maps';
import { GoogleOAuthProvider } from '@react-oauth/google';

import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Stack from '@mui/material/Stack';
import { ThemeProvider } from '@mui/material/styles';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_KEY);

const isProd =
  window.location.hostname.includes('gpanel.promevo.com') ||
  window.location.hostname.includes('promevo-gpanelds.appspot.com');

// The default export renders nothing in production builds, and QA is a
// production Vite build. Load the production bundle off prod hostnames.
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

posthog.init(
  isProd
    ? import.meta.env.VITE_POSTHOG_API_KEY
    : import.meta.env.VITE_POSTHOG_QA_API_KEY,
  {
    api_host: 'https://app.posthog.com',
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    // https://posthog.com/docs/libraries/js
    before_send: (event) => {
      if (!event) {
        return null;
      }

      if (event.event === '$autocapture') {
        return null;
      }

      function redactUrl(value: string): string {
        const roleUrl = pagesConfig.roleManager.link;

        return (
          value
            // remove #
            .replace(/#/g, '')
            // remove query parameters
            .replace(/\?.*$/, '')
            .replace(/users\/[^/]+/, 'users/:userEmail')
            .replace(/groups\/[^/]+/, 'groups/:groupEmail')
            .replace(/my-groups\/groups\/[^/]+/, 'my-groups/groups/:groupEmail')
            .replace(
              /all-groups\/groups\/[^/]+/,
              'all-groups/groups/:groupEmail'
            )
            .replace(new RegExp(`${roleUrl}/[^/]+`), `${roleUrl}/:roleName`)
        );
      }

      function redactObject(
        objectToRedact: Record<string, unknown>
      ): Record<string, unknown> {
        return Object.entries(objectToRedact).reduce((acc, [key, value]) => {
          const redactedValue =
            key.includes('url') && typeof value === 'string'
              ? redactUrl(value)
              : value;

          // @ts-expect-error - this is a workaround to avoid type errors
          acc[redactUrl(key)] = redactedValue;
          return acc;
        }, {});
      }

      const redactedProperties = redactObject(event.properties || {});
      event.properties = redactedProperties;

      if (event.event === '$$heatmap') {
        // $heatmap data is keyed by url
        event.properties.$heatmap_data = redactObject(
          event.properties.$heatmap_data || {}
        );
      }

      const redactedSet = redactObject(event.$set || {});
      event.$set = redactedSet;

      const redactedSetOnce = redactObject(event.$set_once || {});
      event.$set_once = redactedSetOnce;

      return event;
    },
  }
);

const cache = createCache({
  key: 'css',
  prepend: true,
  nonce: '2726c7f26c',
});

function App() {
  const { colorMode } = useStore();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const theme = useMemo(
    () => (colorMode === 'light' || isLoginPage ? lightTheme : darkTheme),
    [colorMode, isLoginPage]
  );

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline enableColorScheme />
          <PostHogProvider client={posthog}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <GoogleOAuthProvider
                clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}
              >
                <Suspense
                  fallback={
                    <Stack
                      sx={{
                        height: '100vh',
                        width: '100vw',
                        bgcolor: 'grey.900',
                      }}
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                    >
                      <img
                        src={GpanelLogoLight}
                        alt="gPanel Logo"
                        width={300}
                      />
                      <CircularProgress />
                    </Stack>
                  }
                >
                  <Container
                    sx={{
                      width: '100%',
                      minHeight: '100vh',
                      backgroundColor: 'background.default',
                      '&.MuiContainer-root': {
                        p: 0,
                      },
                    }}
                    maxWidth={false}
                  >
                    <Outlet />
                    <ShowableDialog />
                    <SharedSnackbar />
                  </Container>
                </Suspense>
                {/* Local */}
                {import.meta.env.DEV && (
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-left"
                  />
                )}
                {/* QA */}
                {!import.meta.env.DEV && !isProd && (
                  <Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction
                      initialIsOpen={false}
                      buttonPosition="bottom-left"
                    />
                  </Suspense>
                )}
              </GoogleOAuthProvider>
            </LocalizationProvider>
          </PostHogProvider>
        </ThemeProvider>
      </CacheProvider>
    </APIProvider>
  );
}

export default App;
